import baseAPI from "@/api/base";
import { AxiosResponse } from "axios";
import type { OutreachCueCardsResponse, OutreachHistoryItem } from "@/types/outreach-cue-cards";
import type {
  AIReplyRequest,
  AIFollowupRequest,
  AIComposeRequest,
  AIRefineRequest,
  AIContentResponse,
} from "@/types/ai-conversation";

interface AIGenerateRequest {
  contactId: string;
  icpId: string;
  channel: string;
}

interface AIGenerateResponseData {
  id: string;
  channel: string;
  subject?: string;
  body: string;
}

interface GenerateAllOutreachRequest {
  contactId: string;
  icpId: string;
  force?: boolean;
}

class AIGenerationService {
  static generateContent = async (
    payload: AIGenerateRequest
  ): Promise<AxiosResponse<{ status: boolean; data: AIGenerateResponseData; msg: string }>> => {
    const response = await baseAPI.post("/ai-personalization/generate", payload);
    return response;
  };

  static generateAllOutreach = async (
    payload: GenerateAllOutreachRequest
  ): Promise<OutreachCueCardsResponse> => {
    const response = await baseAPI.post("/ai-personalization/generate-all", payload, {
      timeout: 120000,
    });
    return response.data.data;
  };

  static toggleSaveOutreach = async (id: string): Promise<{ id: string; isSaved: boolean }> => {
    const response = await baseAPI.patch(`/ai-personalization/${id}/save`);
    return response.data.data;
  };

  static getOutreachHistory = async (contactId: string): Promise<OutreachHistoryItem[]> => {
    const response = await baseAPI.get(`/ai-personalization/history`, {
      params: { contactId },
    });
    return response.data.data;
  };

  static generateReply = async (payload: AIReplyRequest): Promise<AIContentResponse> => {
    const response = await baseAPI.post("/ai-personalization/reply", payload);
    return response.data.data;
  };

  static generateFollowup = async (payload: AIFollowupRequest): Promise<AIContentResponse> => {
    const response = await baseAPI.post("/ai-personalization/followup", payload);
    return response.data.data;
  };

  static generateCompose = async (payload: AIComposeRequest): Promise<AIContentResponse> => {
    const response = await baseAPI.post("/ai-personalization/compose", payload);
    return response.data.data;
  };

  static generateRefine = async (payload: AIRefineRequest): Promise<AIContentResponse> => {
    const formData = new FormData();
    formData.append("contactId", payload.contactId);
    formData.append("channel", payload.channel);
    formData.append("currentDraft", payload.currentDraft);
    formData.append("customInstructions", payload.customInstructions);

    if (payload.icpId) {
      formData.append("icpId", payload.icpId);
    }
    if (payload.currentSubject) {
      formData.append("currentSubject", payload.currentSubject);
    }
    if (payload.conversationHistory) {
      formData.append("conversationHistory", JSON.stringify(payload.conversationHistory));
    }
    if (payload.files?.length) {
      payload.files.forEach((file) => {
        formData.append("files", file);
      });
    }

    const response = await baseAPI.post("/ai-personalization/refine", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
    return response.data.data;
  };
}

export default AIGenerationService;
